'use client';

import { useEffect, useState, useCallback } from 'react';
import {
  DEFAULT_PRICE_ROUNDING,
  applyRounding,
  type PriceRoundingConfig,
} from '@/lib/price-rounding';

let cached: PriceRoundingConfig | null = null;
let inFlight: Promise<PriceRoundingConfig> | null = null;

async function fetchConfig(): Promise<PriceRoundingConfig> {
  if (cached) return cached;
  if (inFlight) return inFlight;
  inFlight = fetch('/api/settings/public/price-rounding', { cache: 'no-store' })
    .then((r) => r.json())
    .then((j) => {
      const cfg = (j?.success && j.data && typeof j.data === 'object')
        ? j.data as PriceRoundingConfig
        : DEFAULT_PRICE_ROUNDING;
      cached = cfg;
      return cfg;
    })
    .catch(() => DEFAULT_PRICE_ROUNDING)
    .finally(() => { inFlight = null; });
  return inFlight;
}

/**
 * Hook globale per il rounding prezzi. Carica una sola volta + cache module-level.
 * Espone `round(value)` per applicare la regola, e `formatPrice(value)` per formattare
 * direttamente in stringa €.
 */
export function usePriceRounding() {
  const [config, setConfig] = useState<PriceRoundingConfig>(cached || DEFAULT_PRICE_ROUNDING);

  useEffect(() => {
    let alive = true;
    fetchConfig().then((c) => { if (alive) setConfig(c); });
    return () => { alive = false; };
  }, []);

  const round = useCallback((value: number | null | undefined): number => applyRounding(value, config), [config]);

  const formatPrice = useCallback((value: number | null | undefined, opts?: { withSign?: boolean }): string => {
    const r = round(value);
    const sign = opts?.withSign ? '€' : '';
    if (config.precision >= 1) {
      // No decimali quando step >= 1 €
      return `${sign}${r.toLocaleString('it-IT', { maximumFractionDigits: 0 })}`;
    }
    return `${sign}${r.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }, [round, config.precision]);

  return { config, round, formatPrice };
}

/** Invalidate cache (chiamato dopo save admin). */
export function invalidatePriceRoundingCache() {
  cached = null;
}
